import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Define the available sections
export type SectionType = 'automotive' | 'everything-else';

export interface Section {
  id: SectionType;
  label: string;
  icon: string;
  description: string;
  basePath: string;
}

// Section definitions
export const SECTIONS: Record<SectionType, Section> = {
  automotive: {
    id: 'automotive',
    label: 'Automotive',
    icon: '🚗',
    description: 'Car services, mechanics, parts & more',
    basePath: '/'
  },
  'everything-else': {
    id: 'everything-else',
    label: 'Everything Else',
    icon: '✨',
    description: 'Food, real estate, tech & more',
    basePath: '/everything'
  }
};

interface SectionContextType {
  currentSection: SectionType;
  setSection: (section: SectionType) => void;
  sections: typeof SECTIONS;
  getSectionByPath: (path: string) => SectionType;
}

const SectionContext = createContext<SectionContextType | undefined>(undefined);

interface SectionProviderProps {
  children: ReactNode;
}

export const SectionProvider: React.FC<SectionProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine initial section from URL
  const getSectionByPath = useCallback((path: string): SectionType => {
    if (path.startsWith('/everything')) {
      return 'everything-else';
    }
    return 'automotive';
  }, []);

  const [currentSection, setCurrentSection] = useState<SectionType>(() => 
    getSectionByPath(location.pathname)
  );

  // Update section when URL changes
  useEffect(() => {
    const newSection = getSectionByPath(location.pathname);
    if (newSection !== currentSection) {
      setCurrentSection(newSection);
    }
  }, [location.pathname, getSectionByPath, currentSection]);

  // Switch section and navigate to the section's homepage
  const setSection = useCallback((section: SectionType) => {
    setCurrentSection(section);
    const targetPath = SECTIONS[section].basePath;
    
    // Always navigate to the section's base path (homepage)
    // This ensures clicking a tab always brings the user to the section's main feed
    navigate(targetPath);
  }, [navigate]);

  // Persist section preference
  useEffect(() => {
    localStorage.setItem('preferredSection', currentSection);
  }, [currentSection]);

  const value: SectionContextType = {
    currentSection,
    setSection,
    sections: SECTIONS,
    getSectionByPath
  };

  return (
    <SectionContext.Provider value={value}>
      {children}
    </SectionContext.Provider>
  );
};

// Custom hook to use section context
export const useSection = (): SectionContextType => {
  const context = useContext(SectionContext);
  if (context === undefined) {
    throw new Error('useSection must be used within a SectionProvider');
  }
  return context;
};

// Safe version that returns null if not in provider (for use in Header)
export const useSectionSafe = (): SectionContextType | null => {
  const context = useContext(SectionContext);
  return context ?? null;
};

export default SectionContext;
